<template>
    <div :class="theme" v-if="initialLoadComplete">
        <div class="w-full mx-auto view-checkout">
            <div class="bg-white">
                <div class="progress">
                    <div class="w-2/3 progress-bar">
                        <div class="progress-value">2/3</div>
                    </div>
                </div>
            </div>

            <div class="checkout-content p-0 mx-auto w-100">
                <div class="flex-1 text-center">
                    <h1>Har du fått med allt du behöver?</h1>
                </div>
                <div v-if="sharableCartId" class="productEntity">
                    <div class="share-cart-info">
                        <InformationCircleIcon class="h-4 mr-2" />
                        <p>Varukorgen delas och uppdateras automatiskt när du eller någon annan lägger till eller tar bort produkter.</p>
                    </div>
                </div>
                <div class="productEntity flex items-center justify-between mt-4 mb-4 min-h-[80px]">
                    <h2 class="m-0">Varukorg</h2>
                    
                    <div class="share">
                        <a v-if="cart && Object.values(cart).length > 0" class="btn-share-cart mx-2 my-2" href="" @click.prevent="shareCart" ref="shareCartButton" :class="{ 'hidden': isShareButtonHidden }">
                            {{ sharableCartId ? 'Dela varukorg' : 'Spara och dela varukorg' }}
                        </a>
                        <div class="dialogue" ref="shareCartDialogue" :class="{ 'active': isShareButtonHidden }">
                            <input type="text" :value="shareUrl" readonly>
                            <div class="shareFilterButtons">
                                <button type="button" id="copyToClipboard" class="input button btn-primary" @click.prevent="copyShareCartLink">Kopiera</button>
                                <button class="input button btn-primary" @click.prevent="shareViaEmail">Dela via e-post</button>
                                <a style="cursor:pointer" @click.prevent="closeShareCartDialogue">Stäng</a>
                            </div>
                        </div>
                    </div>

                </div>
                <form>
                    <div class="order-summary">
                        <h2 class="sr-only">Artiklar i din varukorg:</h2>
                        <h3 v-if="!cart || cart.length === 0 " class="m-4">
                            Din varukorg är tom
                        </h3>

                        <ul role="list" class="grid gap-3">
                            <article v-for="item in cart" :key="item.product_id" class="productEntity gap-4">
                                <div class="product-image" v-if="getProductImage(item)">
                                    <img
                                        class="object-cover object-center w-full h-full"
                                        :src="getProductImage(item)"
                                        :alt="item.name"
                                    />
                                </div>

                                <div class="product-content flex flex-col lg:flex-row">
                                    <div class="text">
                                        <h3 class="title">{{ item.name }}</h3>
                                        <p class="description mb-4">{{ formatDescription(item.description) }}</p>
                                    </div>

                                    <div class="actions lg:items-center">
                                        <button
                                            class="input button btn-primary btn-plus"
                                            :class="{'disabled cursor-not-allowed' : item.quantity === 1}"
                                            @click.prevent="decreaseQuantity(item)"
                                        >
                                            <MinusIcon class="h-4 cursor-pointer" />
                                        </button>

                                        <div class="input number">
                                            <input
                                                type="number"
                                                name="quantity[]"
                                                class="block w-20 border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm py-[11px] min-w[80px]"
                                                min="1"
                                                :max="getMaxQuantity(item)"
                                                :value="item.quantity"
                                                @input="updateQuantity(item, $event)"
                                                :data-current-quantity="item.quantity"
                                            />
                                        </div>

                                        <button
                                            class="input button btn-primary btn-minus"
                                            @click.prevent="increaseQuantity(item)"
                                        >
                                            <PlusIcon class="h-4 cursor-pointer" />
                                        </button>
                                        <button
                                            class="input button button--outlined btn-remove button--noborder js-removeProductFromCart"
                                            @click.prevent="removeProduct(item)"
                                        >
                                            <TrashIcon class="h-4" />
                                        </button>
                                        <div class="delete ml-4">
                                            <button
                                                class="input button btn-primary btn-delete js-removeProductFromCart"
                                                @click.prevent="removeProduct(item)"
                                            >
                                                <TrashIcon class="h-4" /><span>Ta bort</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </article>
                        </ul>
                    </div>
                    <div class="flex items-center justify-center my-4">
                        <a href="" class="btn-order-more mx-2" @click.prevent="orderMore">Gå tillbaka och beställ mer</a>
                        <a v-if="cart && Object.values(cart).length > 0" href="" class="btn-primary mx-2" @click.prevent="setShowForm">Slutför beställning</a>
                    </div>

                    <!-- Order form -->
                    <div v-if="showForm" class="mt-10 order-form">
                        <div class="w-full px-4 mx-auto md:w-533">
                            <div class="mb-10 text-center">
                                <h2 class="mb-4 text-2xl font-semibold">
                                    Fyll i leveransinformation
                                </h2>

                                <p class="contact-info-heading">
                                    <strong>OBS!</strong> Kom ihåg att ange vårdcentral, klinik, företag eller dylikt i fältet Arbetsplats.
                                    På så sätt säkerställer vi att materialet kommer fram.
                                </p>
                            </div>

                            <div class="w-full">
                                <form>
                                    <div>
                                        <div class="mt-4" style="position:relative">
                                            <label for="Workplace">Arbetsplats*</label>

                                            <div class="mt-1">
                                                <AutoComplete
                                                    v-model="form.workplace"
                                                    id="Workplace"
                                                    label="name"
                                                    field="label"
                                                    name="workplace"
                                                    class="focus:border-grayDark-100 block w-full p-0 border-grayDark-100 autocomplete-workplace"
                                                    :suggestions="filteredWorkplaces"
                                                    :dropdown="true"
                                                    @focus="formGotFocus($event)"
                                                    @blur="trackInputField('Company', $event, 1)"
                                                    @complete="searchWorkplace($event)"
                                                />
                                                <button style="position:absolute; right:19px;top:50px;" v-tooltip="'Förslag på vårdenheter kommer att presenteras när du börjar skriva. Om din vårdenhet inte finns med på listan, så kommer den nu att sparas, och finns med till nästa gång du lägger en beställning.'">
                                                    <Icon name="info" class="flex-shrink-0 w-4 h-4 ml-2 fill-gray-400" />
                                                </button>
                                            </div>

                                            <div v-if="form.errors.workplace" class="form-error">
                                                Arbetsplats är obligatorisk
                                            </div>
                                        </div>

                                        <div class="mt-4">
                                            <label for="Name">Namn*</label>

                                            <div class="mt-1">
                                                <input
                                                    v-model="form.name"
                                                    type="text"
                                                    id="Name"
                                                    name="email-address"
                                                    autocomplete="Name"
                                                    class="focus:border-grayDark-100 block w-full pr-12 border-grayDark-100 py-2.5 pl-4"
                                                    style="box-shadow: none"
                                                    required
                                                />
                                                <!-- NOTE THAT FIELDS THAT CAN IDENTIFY A USER SHOULD NOT BE TRACKED -->
                                            </div>
                                            <div v-if="form.errors.name" class="form-error">
                                                Namn är obligatoriskt
                                            </div>
                                        </div>

                                        <div class="mt-4">
                                            <label for="Role">Befattning*</label>
                                            <div class="mt-1">
                                                <select
                                                    v-model="form.role"
                                                    id="Role"
                                                    name="role"
                                                    autocomplete="Role"
                                                    class="focus:border-grayDark-100 block w-full pr-12 border-grayDark-100 py-2.5 pl-4"
                                                    style="box-shadow: none"
                                                    required
                                                    @change="trackInputField('Role', $event, 2)"
                                                >
                                                    <option value="">- Välj -</option>
                                                    <option value="Läkare">Läkare</option>
                                                    <option value="Sjuksköterska">Sjuksköterska</option>
                                                    <option value="Undersköterska">Undersköterska</option>
                                                    <option value="Verksamhetschef">Verksamhetschef</option>
                                                    <option value="Annan">Annan</option>
                                                </select>
                                            </div>

                                            <div v-if="form.errors.role" class="form-error">
                                                Befattning är obligatoriskt
                                            </div>
                                        </div>

                                        <div class="mt-4">
                                            <label for="Speciality">Specialistområde*</label>
                                            <div class="mt-1">
                                                <select
                                                    v-model="form.speciality"
                                                    id="Speciality"
                                                    name="speciality"
                                                    autocomplete="Speciality"
                                                    class="focus:border-grayDark-100 block w-full pr-12 border-grayDark-100 py-2.5 pl-4"
                                                    style="box-shadow: none"
                                                    required
                                                    @change="trackInputField('Speciality', $event, 3)"
                                                >
                                                    <option value="">- Välj -</option>
                                                    <option value="Akutsjukvård">Akutsjukvård</option>
                                                    <option value="Allergologi">Allergologi</option>
                                                    <option value="Allmänmedicin">Allmänmedicin</option>
                                                    <option value="Anestesi &amp; Intensivvård">Anestesi &amp; Intensivvård</option>
                                                    <option value="Barn- och ungdomsmedicin">Barn- och ungdomsmedicin</option>
                                                    <option value="Barn- och ungdomspsykiatri">Barn- och ungdomspsykiatri</option>
                                                    <option value="Dermatologi">Dermatologi</option>
                                                    <option value="Diabetologi">Diabetologi</option>
                                                    <option value="Endokrinologi">Endokrinologi</option>
                                                    <option value="Farmakologi">Farmakologi</option>
                                                    <option value="Företagshälsovård">Företagshälsovård</option>
                                                    <option value="Gastroenterologi">Gastroenterologi</option>
                                                    <option value="Geriatrik">Geriatrik</option>
                                                    <option value="Gynekologi">Gynekologi</option>
                                                    <option value="Gynekologisk onkologi">Gynekologisk onkologi</option>
                                                    <option value="Handkirurgi">Handkirurgi</option>
                                                    <option value="Hematologi">Hematologi</option>
                                                    <option value="Infektion">Infektion</option>
                                                    <option value="Internmedicin">Internmedicin</option>
                                                    <option value="Kardiologi">Kardiologi</option>
                                                    <option value="Kirurgi">Kirurgi</option>
                                                    <option value="Klinisk lab spec">Klinisk lab spec</option>
                                                    <option value="Lungmedicin">Lungmedicin</option>
                                                    <option value="Nefrologi">Nefrologi</option>
                                                    <option value="Neonatologi">Neonatologi</option>
                                                    <option value="Neurokirurgi">Neurokirurgi</option>
                                                    <option value="Neurologi">Neurologi</option>
                                                    <option value="Onkologi">Onkologi</option>
                                                    <option value="Ortopedi">Ortopedi</option>
                                                    <option value="Patologi">Patologi</option>
                                                    <option value="Pediatrik">Pediatrik</option>
                                                    <option value="Plastikkirurgi">Plastikkirurgi</option>
                                                    <option value="Psykiatri">Psykiatri</option>
                                                    <option value="Radiologi">Radiologi</option>
                                                    <option value="Rehabilitering">Rehabilitering</option>
                                                    <option value="Reumatologi">Reumatologi</option>
                                                    <option value="Rättsmedicin">Rättsmedicin</option>
                                                    <option value="Socialmedicin">Socialmedicin</option>
                                                    <option value="Thoraxkirurgi">Thoraxkirurgi</option>
                                                    <option value="Urologi">Urologi</option>
                                                    <option value="Yrkes- och miljömedicin">Yrkes- och miljömedicin</option>
                                                    <option value="Ögon">Ögon</option>
                                                    <option value="ÖNH">ÖNH</option>
                                                </select>
                                            </div>

                                            <div v-if="form.errors.speciality" class="form-error">
                                                Specialistområde är obligatoriskt
                                            </div>
                                        </div>

                                        <div class="mt-4">
                                            <label for="Region">Region*</label>

                                            <div class="mt-1">
                                                <BaseSelect
                                                    v-model="form.region"
                                                    id="region"
                                                    name="region"
                                                    autocomplete="region"
                                                    class="focus:border-grayDark-100 block w-full pr-12 border-grayDark-100 py-2.5 pl-4"
                                                    :options="regionOptions"
                                                    required
                                                    @change="trackInputField('Region', $event, 4)"
                                                />
                                            </div>

                                            <div v-if="form.errors.region" class="form-error">
                                                Region är obligatoriskt
                                            </div>
                                        </div>

                                        <div class="mt-4">
                                            <label for="address">Adress*</label>

                                            <div class="mt-1">
                                                <input
                                                    v-model="form.address"
                                                    type="text"
                                                    id="address"
                                                    name="address"
                                                    autocomplete="street-address"
                                                    class="focus:border-grayDark-100 block w-full pr-12 border-grayDark-100 py-2.5 pl-4"
                                                    style="box-shadow: none"
                                                    required
                                                />
                                                <!-- NOTE THAT FIELDS THAT CAN IDENTIFY A USER SHOULD NOT BE TRACKED -->
                                            </div>

                                            <div v-if="form.errors.address" class="form-error">
                                                Adress är obligatorisk
                                            </div>
                                        </div>

                                        <div class="mt-4">
                                            <label for="zip-code">Postnummer*</label>

                                            <div class="mt-1">
                                                <input
                                                    v-model="form.zip_code"
                                                    id="zip-code"
                                                    name="zip-code"
                                                    type="text"
                                                    autocomplete="zip-code"
                                                    class="focus:border-grayDark-100 block w-full pr-12 border-grayDark-100 py-2.5 pl-4"
                                                    style="box-shadow: none"
                                                    required
                                                />
                                                <!-- NOTE THAT FIELDS THAT CAN IDENTIFY A USER SHOULD NOT BE TRACKED -->
                                            </div>
                                            <div v-if="form.errors.zip_code" class="form-error">
                                                Postnummer är obligatoriskt
                                            </div>
                                        </div>

                                        <div class="mt-4">
                                            <label for="city">Stad*</label>

                                            <div class="mt-1">
                                                <input
                                                    v-model="form.city"
                                                    id="city"
                                                    type="text"
                                                    name="city"
                                                    autocomplete="address-level2"
                                                    class="focus:border-grayDark-100 block w-full pr-12 border-grayDark-100 py-2.5 pl-4"
                                                    style="box-shadow: none"
                                                    required
                                                />
                                                <!-- NOTE THAT FIELDS THAT CAN IDENTIFY A USER SHOULD NOT BE TRACKED -->
                                            </div>

                                            <div v-if="form.errors.city" class="form-error">
                                                Stad är obligatorisk
                                            </div>
                                        </div>

                                        <div class="mt-4">
                                            <label for="email-address">E-post*</label>

                                            <div class="mt-1">
                                                <input
                                                    v-model="form.email"
                                                    type="email"
                                                    id="email-address"
                                                    name="email-address"
                                                    autocomplete="email"
                                                    class="focus:border-grayDark-100 block w-full pr-12 border-grayDark-100 py-2.5 pl-4"
                                                    style="box-shadow: none"
                                                    required
                                                />
                                                <!-- NOTE THAT FIELDS THAT CAN IDENTIFY A USER SHOULD NOT BE TRACKED -->
                                            </div>

                                            <div v-if="form.errors.email" class="form-error">
                                                E-post är obligatorisk
                                            </div>
                                        </div>

                                        <div class="mt-4">
                                            <label for="phone">{{ parentCategory?.id == 8 ? 'Mobilnummer' : 'Telefonnummer' }}*</label>

                                            <div class="mt-1">
                                                <input
                                                    v-model="form.phone"
                                                    type="tel"
                                                    id="phone"
                                                    name="phone"
                                                    autocomplete="phone"
                                                    class="focus:border-grayDark-100 block w-full pr-12 border-grayDark-100 py-2.5 pl-4"
                                                    style="box-shadow: none"
                                                    required
                                                />
                                                <!-- NOTE THAT FIELDS THAT CAN IDENTIFY A USER SHOULD NOT BE TRACKED -->
                                            </div>

                                            <div v-if="form.errors.phone" class="form-error">
                                                {{ parentCategory?.id == 8 ? 'Mobilnummer är obligatoriskt och måste vara ett giltigt svenskt mobilnummer.' : 'Telefonnummer är obligatoriskt' }}
                                            </div>
                                        </div>

                                        <div class="mt-4">
                                            <span style="display:block;margin:10px 0 30px;font-size:16px;font-style:italic">
                                                *Samtliga fält som är markerade måste fyllas i innan formuläret skickas.
                                            </span>
                                        </div>

                                        <div class="mt-4">
                                            <label>Min signatur*</label>
                                            <div class="flex justify-start input checkmark mt-4">
                                                <input
                                                    v-model="form.policy"
                                                    id="confirm-checkout"
                                                    type="checkbox"
                                                    style="width: 20px;height: 20px;position: absolute;box-shadow: none"
                                                    name="confirm_checkout"
                                                    value="1"
                                                    class="input js-validateDisclaimer"
                                                    @click="tickingDisclaimer($event)"
                                                />

                                                <label
                                                    v-if="parentCategory && parentCategory.id == 1"
                                                    class="policy-label"
                                                    for="confirm-checkout"
                                                >
                                                    Jag bekräftar härmed att jag tagit del av och förstått innehållet i Pfizers Personuppgiftspolicy.
                                                    För mer information om vår personuppgiftsbehandling,
                                                    se vår <a href="https://privacycenter.pfizer.com/sv/sweden" target="_blank">Personuppgiftspolicy</a>.
                                                </label>
                                                <label
                                                    v-if="parentCategory && parentCategory.id != 1"
                                                    class="policy-label"
                                                    for="confirm-checkout"
                                                >
                                                    Jag har läst och förstått tillämplig <a href="https://privacycenter.pfizer.com/sv/hcp" target="_blank">Integritetspolicy</a>,
                                                    <a href="https://privacycenter.pfizer.com/sv/sweden#cookies" target="_blank">Cookiepolicy</a> och accepterar
                                                    <a href="https://www.pfizer.se/anvandarvillkor" target="_blank">Användarvillkoren</a>.
                                                </label>
                                            </div>

                                            <div v-if="form.errors.policy" class="form-error">
                                                Du måste godkänna villkoren
                                            </div>
                                        </div>

                                        <div class="mt-4">
                                            <div class="flex justify-start input checkmark">
                                                <input
                                                    v-model="remember"
                                                    id="confirm-remember"
                                                    type="checkbox"
                                                    style="width: 20px;height: 20px;position: absolute;box-shadow: none"
                                                    name="confirm_remember"
                                                    value="1"
                                                    class="input js-validateDisclaimer"
                                                />

                                                <label
                                                    class="policy-label"
                                                    for="confirm-remember"
                                                >
                                                    Kom ihåg mina uppgifter till nästa gång

                                                    <button v-tooltip="'Genom att kryssa i boxen, godkänner du att en cookie sparas med dina beställningsuppgifter. En service som vi erbjuder dig, för en smidigare beställningsprocess. Denna cookie kan du ta du bort via webbläsarinställningen eller så sker det automatiskt av webbläsaren beroende på din vårdenhets IT-policy.'">
                                                        <Icon name="questionmark" class="flex-shrink-0 w-4 h-4 ml-2 fill-gray-400" />
                                                    </button>
                                                </label>
                                            </div>

                                            <div v-if="form.errors.policy" class="form-error">
                                                Du måste godkänna villkoren
                                            </div>
                                        </div>

                                        <div class="mt-4">
                                            <div class="flex justify-start input checkmark">
                                                <input
                                                    v-model="form.reminder"
                                                    id="confirm-reminder"
                                                    type="checkbox"
                                                    style="width: 20px;height: 20px;position: absolute;box-shadow: none"
                                                    name="confirm_reminder"
                                                    value="1"
                                                    class="input js-validateDisclaimer"
                                                />

                                                <label
                                                    class="policy-label"
                                                    for="confirm-reminder"
                                                >
                                                    Skicka mig påminnelse när det är dags att beställa igen

                                                    <button v-tooltip="'Genom att kryssa i boxen, godkänner du att vi sparsamt skickar ut påminnelser via e-post om att det är dags att fylla på ditt materiallager.'">
                                                        <Icon name="questionmark" class="flex-shrink-0 w-4 h-4 ml-2" />
                                                    </button>
                                                </label>
                                            </div>

                                            <div v-if="form.errors.policy" class="form-error">
                                                Du måste godkänna villkoren
                                            </div>
                                        </div>
                                        <div class="mt-8" :class="parentCategory?.id == 1 ? '' : 'hidden'">
                                            <h3 class="mt-2 font-bold">Jag vill också prenumerera på nyhetsbrev anpassat för</h3>
                                            <div class="flex justify-start mt-4 input checkmark">
                                                <input
                                                    v-model="form.interestPrimaryCare"
                                                    id="primary-care"
                                                    type="checkbox"
                                                    style="width: 20px;height: 20px;position: absolute;box-shadow: none"
                                                    name="primary-care"
                                                    value="1"
                                                    class="input"
                                                    @click="trackNewsletter($event, 'primary care')" 
                                                />

                                                <label
                                                    class="policy-label"
                                                    for="primary-care"
                                                >Primärvård</label>
                                            </div>
                                            <div class="flex justify-start mt-4 input checkmark">
                                                <input
                                                    v-model="form.interestCardiology"
                                                    id="cardiology"
                                                    type="checkbox"
                                                    style="width: 20px;height: 20px;position: absolute;box-shadow: none"
                                                    name="cardiology"
                                                    value="1"
                                                    class="input"
                                                    @click="trackNewsletter($event, 'cardiology')"
                                                />

                                                <label
                                                    class="policy-label"
                                                    for="cardiology"
                                                >Kardiologi</label>
                                            </div>
                                            <div class="flex justify-start mt-4 input checkmark">
                                                <input
                                                    v-model="form.interestInternalMedicine"
                                                    id="internal-medicine"
                                                    type="checkbox"
                                                    style="width: 20px;height: 20px;position: absolute;box-shadow: none"
                                                    name="internal-medicine"
                                                    value="1"
                                                    class="input"
                                                    @click="trackNewsletter($event, 'internal medicine')"
                                                />

                                                <label
                                                    class="policy-label"
                                                    for="internal-medicine"
                                                >Internmedicin</label>
                                            </div>
                                            <div class="mt-4">
                                                <small>Genom att fylla i ett intresseområde skriver jag upp mig som prenumerant på Eliquis nyhetsbrev som innehåller terapi- och produktinriktad
                                                    information/marknadsföring från Pfizer AB baserat på mitt specialistområde och närliggande områden. Information/marknadsföring relaterat och
                                                    närliggande till området antikoagulation och produkten Eliquis ® kommer även fortsättningsvis att komma från både Pfizer AB och Bristol Myers Squibb AB. *
                                                </small>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>

                        <div class="flex items-center justify-center mt-4">
                            <LoadingButton
                                :loading="loading"
                                class="btn-create-order disabled:opacity-25"
                                :disabled="!form.policy"
                                @click.prevent="createOrder"
                            >
                                Skicka
                            </LoadingButton>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
    import { ref, watch, onMounted } from 'vue'
    import AutoComplete 					  from 'primevue/autocomplete'
    import { PlusIcon, MinusIcon, TrashIcon, InformationCircleIcon } from '@heroicons/vue/outline'
    import LoadingButton 					  from '@/Shared/LoadingButton'
    import Icon 					  		  from '@/Shared/Icon'
    import BaseSelect                         from '@/Shared/BaseSelect'
    import AdobeTracking                      from '@/Shared/AdobeTracking'

    export default {
        name: 'Checkout',
        components: {
            AutoComplete,
            PlusIcon,
            MinusIcon,
            TrashIcon,
            InformationCircleIcon,
            LoadingButton,
            Icon,
            BaseSelect
        },
        props: {
            parentCategory: {
                type: Object,
                required: true,
            },
            childCategory: {
                type: Object,
                required: false,
            },
            regions: {
                type: [Array, Object],
                required: true,
            },
            cart: {
                type: Object,
                required: false,
            },
            sharableCartId: {
                type: String,
                required: false,
            },
            parentDomain: {
                type: String,
                required: false,
            },
        },
        setup(props, { emit }) {
            const form = ref({
                errors: {},
                workplace: null,
                name: '',
                role: '',
                speciality: '',
                region: '',
                email: '',
                phone: '',
                address: '',
                city: '',
                zip_code: '',
                policy: false,
                reminder: true,
                interestPrimaryCare: false,
                interestCardiology: false,
                interestInternalMedicine: false,
                interestNurses: false
            })
            const cart = ref(props.cart)
            const sharableCartId = ref(props.sharableCartId)
            const parentDomain = ref(props.parentDomain)
            const loading = ref(false)
            const remember = ref(false)
            const formInitatedTracked = ref(false)
            const showForm = ref(false)
            const initialLoadComplete = ref(false)
            const products = ref([])
            const filteredWorkplaces = ref(null)
            const workplaces = ref([])
            const shareUrl = ref('')
            const isShareButtonHidden = ref(false)

            const {track} = AdobeTracking();

            watch(() => props.cart, (newCart) => {
                cart.value = newCart
            }, { immediate: true })

            watch(() => props.sharableCartId, (newSharableCartId) => {
                sharableCartId.value = newSharableCartId
            }, { immediate: true })

            watch(() => props.parentDomain, (newParentDomain) => {
                parentDomain.value = newParentDomain
            }, { immediate: true })

            onMounted(() => {
                if (getCookie('pf-order')) {
                    form.value = getCookie('pf-order')
                    remember.value = true
                }

                loadInitialValues()

                // Send event to calculate new height of iframe
                window.postMessage('iframeCheckout', '*');
                
            })

            const formatDescription = (string) => {
                if (!string) return '';
                let description = string.replace(/<\/?[^>]+>/ig, " ");
                
                if (description.length > 90) {
                    description = description.substring(0, 90) + '...';
                }

                return description;
            }

            const getRegionsForDropdown = (regions) => {
                let regionsForDropdown = regions.map(region => {
                    return {
                        name: region.name,
                        value: region.id
                    }
                })

                regionsForDropdown.unshift({
                    name: '- Välj -',
                    value: ''
                })

                return regionsForDropdown
            }

            const getProductImage = (item) => {
                return item.image_url
            }

            const trackCheckout = (pageLoad = true) => {
                const cart = localStorage.getItem('pf-cart') ? JSON.parse(localStorage.getItem('pf-cart')) : '[]';
                let productInfo = '';
                for (const [key, item] of Object.entries(cart)) {
                    // [category];[product name];[quantity]
                    productInfo += `,;${item.name};${item.quantity}`;
                }

                if (productInfo == '') return;

                track('pfMaterialOrder',
                    {       
                        'productInfo': productInfo.substring(1), //value format is ';{product name};{quantity}'
                        'orderAction': 'checkout', // actions are: cart addition, cart removal, checkout, completed
                        'orderID': '0', //Order ID, mandatory for completed action
                        'userID': '0', //User ID, Customer ID, GRV ID
                        'userType': 'generic', // GRV, PAC, DIN, generic			
                        'materialOrderPageLoad': pageLoad ? 'true' : 'false' //hardcode to false for clicks
                    }
                );
            }

            const trackNewsletter = (e, which) => {
                if (!e.srcElement.checked) return;
                track('pfLinkName',
                    {
                        'linkName': 'material page|checkout view|newsletter for ' + which,
                        'linkType': 'internal'
                    }
                );
            }

            const orderMore = () => {
                emit('setActiveView', 'products');

                track('pfLinkName',
                    {
                        'linkName': 'material page|checkout view|back to products',
                        'linkType': 'internal'
                    }
                );
            }

            const createOrder = () => {

                trackCheckout(false);

                if (form.value.workplace && typeof form.value.workplace === 'object') {
                    form.value.workplace = form.value.workplace.label
                }

                if (form.value.policy) {
                    loading.value = true
                    form.value.cart = cart.value
                    form.value.parent_category = props.parentCategory.id

                    track('pfFormWorkflow',
                        {       
                            'formName': 'pfizershopOrderForm',
                            'formAction': 'submitted',
                            'formStep': '1',
                            'formPageLoad': 'false',
                        }
                    );

                    window.dispatchEvent(new CustomEvent('pfizershop', {
                        detail: {
                            action: 'place-order'
                        }
                    }));

                    fetch(process.env.MIX_API_URL+'/cart/checkout', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(form.value),
                    }).then(response => response.json()).then(data => {

                        if (remember.value) {
                            form.value.errors = {}
                            createCookie('pf-order', form.value, 182)
                        }

                        if (data.errors) {
                            form.value.errors = data.errors
                            setTimeout(() => {
                                document.querySelectorAll('.form-error')[0].parentElement.scrollIntoView();
                                let errors = '';

                                document.querySelectorAll('.form-error').forEach((error) => {
                                    errors += error.innerText + '~';
                                });

                                track('pfFormWorkflow',
                                    {       
                                        'formName': 'pfizershopOrderForm',
                                        'formAction': 'error',
                                        'formStep': '1',
                                        'formPageLoad': 'false',
                                        'formErrorMessage': errors.substring(0, errors.length - 1)
                                    }
                                );

                            }, 500);
                        } else {
                            trackInputFields();
                        }

                        if (data.status === 'success') {
                            emit('setActiveView', 'thankyou')
                            emit('setPlacedOrderData', data.data)

                            track('pfFormWorkflow',
                                {       
                                    'formName': 'pfizershopOrderForm',
                                    'formAction': 'completed',
                                    'formStep': '1',
                                    'formPageLoad': 'false',
                                }
                            );

                            const cart = localStorage.getItem('pf-cart') ? JSON.parse(localStorage.getItem('pf-cart')) : '[]';
                            let productInfo = '';
                            for (const [key, item] of Object.entries(cart)) {
                                // [category];[product name];[quantity]
                                productInfo += `,;${item.name};${item.quantity}`;
                            }

                            track('pfMaterialOrder',
                                {       
                                    'productInfo': productInfo.substring(1), //value format is ';{product name};{quantity}'
                                    'orderAction':'completed', // actions are: cart addition, cart removal, checkout, completed
                                    'orderID': `${data.order_id}`, //Order ID, mandatory for completed action
                                    'userID': `${data.customer_id}`, //User ID, Customer ID, GRV ID
                                    'userType': 'generic', // GRV, PAC, DIN, generic			
                                    'materialOrderPageLoad': 'false' //hardcode to false for clicks
                                }
                            );

                            localStorage.setItem('pf-cart', '[]');
                        }
                    }).catch(error => {}).finally(() => {
                        loading.value = false
                        
                        // Send event to calculate new height of iframe
                        window.postMessage('iframeCheckout', '*');
                    })
                }
            }

            const loadInitialValues = async () => {

                fetch(process.env.MIX_API_URL+'/api/clientWorkplaces').then(response => response.json()).then(data => {
                    workplaces.value = data.map(workplace => {
                        return {
                            label: workplace,
                            value: workplace.toLowerCase()
                        }
                    })
                })

                initialLoadComplete.value = true;
            }

            const getMaxQuantity = (item) => {
                let max = products.value.find((product) => {
                    if (product.id === item.product_id) {
                        return product
                    }
                })
                return max?.max_per_order ?? null;
            }

            const updateQuantity = (item, event) => {
                let max = getMaxQuantity(item);
                let currentQuantity = parseInt(event.target.dataset.currentQuantity);
                let newQuantity = parseInt(event.target.value);

                if (newQuantity < 1) {
                    newQuantity = 1;
                }
                if (max && newQuantity >= max) {
                    newQuantity = max;
                }

                if (newQuantity !== currentQuantity) {
                    track('pfMaterialOrder',
                        {       
                            'productInfo': `;${item.name};${Math.abs(newQuantity-currentQuantity)}`, //value format is ';{product name};{quantity}'
                            'orderAction': newQuantity > currentQuantity ? 'cart addition' : 'cart removal', // actions are: cart addition, cart removal, checkout, completed
                            'orderID': '0', //Order ID, mandatory for completed action
                            'userID': '0', //User ID, Customer ID, GRV ID
                            'userType': 'generic', // GRV, PAC, DIN, generic	
                            'materialOrderPageLoad': 'false' //hardcode to false for clicks
                        }
                    );
                }

                fetch(process.env.MIX_API_URL+'/cart/update', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        product_id: item.product_id,
                        quantity: newQuantity,
                        cart: cart.value,
                        sharable_cart_id: sharableCartId.value
                    }),
                }).then(response => response.json()).then(data => {
                    cart.value = data.cart
                    emit('cartUpdated', cart.value)
                    // localStorage.setItem('pf-cart', JSON.stringify(this.cart))
                    // this.trackCheckout(false);
                })
            }

            const increaseQuantity = (item) => {
                let max = getMaxQuantity(item);
                if (max && item.quantity >= max) {
                    item.quantity = max - 1;
                }

                track('pfMaterialOrder',
                    {       
                        'productInfo': `;${item.name};1`, //value format is ';{product name};{quantity}'
                        'orderAction': 'cart addition', // actions are: cart addition, cart removal, checkout, completed
                        'orderID': '0', //Order ID, mandatory for completed action
                        'userID': '0', //User ID, Customer ID, GRV ID
                        'userType': 'generic', // GRV, PAC, DIN, generic	
                        'materialOrderPageLoad': 'false' //hardcode to false for clicks
                    }
                );

                fetch(process.env.MIX_API_URL+'/cart/update', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        product_id: item.product_id,
                        quantity: item.quantity + 1,
                        cart: cart.value
                    }),
                }).then(response => response.json()).then(data => {
                    cart.value = data.cart
                    emit('cartUpdated', cart.value)
                    // localStorage.setItem('pf-cart', JSON.stringify(this.cart))
                    // this.trackCheckout(false)
                })
            }

            const decreaseQuantity = (item) => {
                if (item.quantity === 1) {
                    return
                }

                track('pfMaterialOrder',
                    {       
                        'productInfo': `;${item.name};1`, //value format is ';{product name};{quantity}'
                        'orderAction': 'cart removal', // actions are: cart addition, cart removal, checkout, completed
                        'orderID': '0', //Order ID, mandatory for completed action
                        'userID': '0', //User ID, Customer ID, GRV ID
                        'userType': 'generic', // GRV, PAC, DIN, generic
                        'materialOrderPageLoad': 'false' //hardcode to false for clicks
                    }
                );

                fetch(process.env.MIX_API_URL+'/cart/update', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        product_id: item.product_id,
                        quantity: item.quantity - 1,
                        cart: cart.value
                    })
                }).then(response => response.json()).then(data => {
                    cart.value = data.cart;
                    emit('cartUpdated', cart.value)
                    // localStorage.setItem('pf-cart', JSON.stringify(this.cart));
                    // this.trackCheckout(false);
                })
            }

            const removeProduct = (item) => {
                const quantity = cart.value[item.product_id]?.quantity ?? 0;

                track('pfMaterialOrder',
                    {       
                        'productInfo': `;${item.name};${quantity}`, //value format is ';{product name};{quantity}'
                        'orderAction': 'cart removal', // actions are: cart addition, cart removal, checkout, completed
                        'orderID': '0', //Order ID, mandatory for completed action
                        'userID': '0', //User ID, Customer ID, GRV ID
                        'userType': 'generic', // GRV, PAC, DIN, generic
                        'materialOrderPageLoad': 'false' //hardcode to false for clicks
                    }
                );

                fetch(process.env.MIX_API_URL+'/cart/remove', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        product_id: item.product_id,
                        cart: cart.value
                    })
                }).then((response) => {
                    return response.json();
                }).then((data) => {
                    cart.value = data.cart;
                    emit('cartUpdated', cart.value)    
                    // localStorage.setItem('pf-cart', JSON.stringify(this.cart));
                    // this.trackCheckout(false);
                })
            }

            const searchWorkplace = (event) => {
                setTimeout(() => {
                    if (!event.query.trim().length) {
                        filteredWorkplaces.value = [...workplaces.value];
                    } else {
                        filteredWorkplaces.value = workplaces.value.filter(workplace => {
                            return workplace.label.toLowerCase().startsWith(event.query.toLowerCase())
                        })
                    }
                }, 250)

            }

            const formGotFocus = () => {
                window.dispatchEvent(new CustomEvent('pfizershop', {
                    detail: {
                        action: 'order-form-got-focus'
                    }
                }));

                if (formInitatedTracked.value) return;

                track('pfFormWorkflow',
                    {       
                        'formName': 'pfizershopOrderForm',
                        'formAction': 'initiated',
                        'formStep': '1',
                        'formPageLoad': 'false'
                    }
                );

                formInitatedTracked.value = true;
            }

            const trackInputField = (field, e, pos) => {

                if (field == 'Region') {
                    // Region has to be tracked differently
                    track('pfFormWorkflowInput',
                        {       
                            'formName': 'pfizershopOrderForm',
                            'formStep': '1',
                            'formQuestion': field,
                            'formAnswer': e.target.options[e.target.selectedIndex].text,
                            'formQuestionIndex': pos
                        }
                    );
                    return;
                }

                track('pfFormWorkflowInput',
                    {       
                        'formName': 'pfizershopOrderForm',
                        'formStep': '1',
                        'formQuestion': field,
                        'formAnswer': e.target.value,
                        'formQuestionIndex': pos
                    }
                );
            }

            const tickingDisclaimer = (e) => {
                if(e.srcElement.checked) {
                    window.dispatchEvent(new CustomEvent('pfizershop', {
                        detail: {
                            action: 'agree-to-terms'
                        }
                    }));
                }
            }

            const createCookie = (name, values, days) => {
                values.saveUntil = new Date().getTime() + (days * 24 * 60 * 60 * 1000);
                localStorage.setItem(name, JSON.stringify(values));
            }

            const getCookie = (name) => {

                const cookie = localStorage.getItem(name);

                if (!cookie) {
                    return null
                }

                let savedData = JSON.parse(cookie)

                if (savedData.saveUntil > new Date().getTime()) {
                    return savedData;
                } else {
                    localStorage.removeItem(name);
                }

                return null
            }

            const setShowForm = () => {
                showForm.value = true;
                setTimeout(() => document.querySelector('.order-form').scrollIntoView(true), 500);
                window.dispatchEvent(new CustomEvent('show-order-form'));

                track('pfLinkName',
                    {
                        'linkName': 'material page|checkout view|show customer registration form',
                        'linkType': 'internal'
                    }
                );
            }

            const shareCart = (e) => {
                isShareButtonHidden.value = true;

                fetch(process.env.MIX_API_URL+'/cart/saveSharableCart', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        unique_id: sharableCartId.value,
                        cart: cart.value,
                    }),
                }).then((response) => response.json()).then((data) => {
                    if (data.status === 'success') {
                        sharableCartId.value = data.data.unique_id;
                        emit('setSharableCartId', sharableCartId.value);
                        shareUrl.value = parentDomain.value + '?cid=' + sharableCartId.value;
                    }
                });
            }

            const closeShareCartDialogue = () => {
                isShareButtonHidden.value = false;
            }

            const copyShareCartLink = (e) => {
                e.target.innerText = 'Kopierat!';
                copyToClipboard(shareUrl.value);
            }

            const shareViaEmail = () => {
                var subject = encodeURIComponent(props.parentCategory?.id == 8 ? 'Delad varukorg på pfizerpro.se' : 'Delad varukorg på eliquis.se');
                var body = encodeURIComponent('Jag har delat en varukorg med dig: ' + shareUrl.value);
                window.open('mailto:?subject='+subject+'&body='+body, '_blank');
            }

            const copyToClipboard = (text) => {
                // Modern clipboard API
                if (navigator.clipboard && window.isSecureContext) {
                    navigator.clipboard.writeText(text)
                        .then(() => {
                            // console.log('Text copied successfully using clipboard API');
                        })
                        .catch(err => {
                            fallbackCopyToClipboard(text);
                        });
                } else {
                    // Fallback for older browsers or non-secure contexts
                    fallbackCopyToClipboard(text);
                }
            }

            const fallbackCopyToClipboard = (text) => {
                // Create a temporary textarea element
                const textArea = document.createElement('textarea');
                textArea.value = text;
                
                // Make it invisible
                textArea.style.position = 'fixed';
                textArea.style.left = '-999999px';
                textArea.style.top = '-999999px';
                textArea.style.opacity = '0';
                
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                
                try {
                    const successful = document.execCommand('copy');
                    if (!successful) {
                        showCopyError();
                    }
                } catch (err) {
                    showCopyError();
                }
                
                document.body.removeChild(textArea);
            }

            const showCopyError = () => {
                // Show user-friendly error message
                alert('Kunde inte kopiera länken automatiskt. Vänligen markera och kopiera länken manuellt.');
            }

            const trackInputFields = () => {
                track('pfFormWorkflowInput',
                    {       
                        'formName': 'pfizershopOrderForm',
                        'formStep': '1',
                        'formQuestion': 'Workplace',
                        'formAnswer': form.value.workplace,
                        'formQuestionIndex': '1'
                    }
                );

                track('pfFormWorkflowInput',
                    {       
                        'formName': 'pfizershopOrderForm',
                        'formStep': '1',
                        'formQuestion': 'Role',
                        'formAnswer': form.value.role,
                        'formQuestionIndex': '2'
                    }
                );

                track('pfFormWorkflowInput',
                    {       
                        'formName': 'pfizershopOrderForm',
                        'formStep': '1',
                        'formQuestion': 'Speciality',
                        'formAnswer': form.value.speciality,
                        'formQuestionIndex': '3'
                    }
                );

                track('pfFormWorkflowInput',
                    {       
                        'formName': 'pfizershopOrderForm',
                        'formStep': '1',
                        'formQuestion': 'Region',
                        'formAnswer': document.querySelector('select[name="region"]').options[document.querySelector('select[name="region"]').selectedIndex].text,
                        'formQuestionIndex': '4'
                    }
                );
            }

            return {
                cart,
                sharableCartId,
                shareUrl,
                loading,
                workplaces,
                products,
                regionOptions: getRegionsForDropdown(props.regions),
                filteredWorkplaces,
                remember,
                formInitatedTracked,
                initialLoadComplete,
                theme: props.parentCategory.color_scheme,
                track: track,
                showForm,
                form,
                searchWorkplace,
                getProductImage,
                formatDescription,
                getMaxQuantity,
                updateQuantity,
                increaseQuantity,
                decreaseQuantity,
                removeProduct,
                closeShareCartDialogue,
                copyShareCartLink,
                shareViaEmail,
                copyToClipboard,
                showCopyError,
                orderMore,
                createOrder,
                setShowForm,
                formGotFocus,
                trackInputField,
                tickingDisclaimer,
                createCookie,
                getCookie,
                shareCart,
                isShareButtonHidden
            }
        }
    }
</script>

<style>
    .p-autocomplete-items {
        padding: 10px;
        background: #fff;
        border: 2px solid #eaeaea;
    }

    .p-autocomplete-item {
        display: block;
        padding: 10px;
    }

    .p-autocomplete-item:hover {
        background-color: #eaeaea;
    }

    .p-autocomplete-input {
        background: #fff;
        outline: none !important;
        border: none !important;
        box-shadow: none !important;
    }
</style>